import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import { VisuallyHidden } from './VisuallyHidden.js';
import './StatusPill.css';

export type StatusPillTone = 'neutral' | 'success' | 'info' | 'warning';

export interface StatusPillProps {
  children: ReactNode;
  tone?: StatusPillTone;
  /**
   * What the pill states, for assistive tech — e.g. "Running in Dedupr's cloud".
   * The visible label is short by design ("Cloud"), and a bare place name read
   * out of context does not say what it is describing.
   */
  description?: string;
  /**
   * Makes the pill a button with the prototype's disclosure caret — its mode
   * pill opens an explainer modal. The caret renders ONLY when there is a
   * handler: a caret that opens nothing is a lie, which is why the earlier
   * static pill deliberately omitted it.
   */
  onClick?: () => void;
  'aria-expanded'?: boolean;
}

/**
 * A standing, page-level status pill (design-system catalogue, Tier 2) — the
 * topbar's Cloud / Local mode indicator.
 *
 * Distinct from `Badge` on purpose, and the distinction is worth keeping: a Badge
 * is a 22px uppercase state on a *row* (a severity, a tier, a run outcome), while
 * this is a 36px mixed-case statement about the *whole screen*. Rendering the mode
 * through Badge is what made the topbar say "CLOUD" where the prototype says
 * "Cloud" (design-fidelity audit S-4).
 *
 * Non-interactive here. The prototype's mode pill also opens an explainer modal;
 * that belongs with the trust-graduation work (ADR-041) and is deliberately not
 * faked with a dead caret in the meantime.
 */
export function StatusPill({
  children,
  tone = 'neutral',
  description,
  onClick,
  'aria-expanded': ariaExpanded,
}: StatusPillProps) {
  const body = (
    <>
      {/* Decorative: the tone it encodes is always also in the text, per
          a11y.md §2's "never colour alone". */}
      <span className="dpr-status-pill__dot" aria-hidden="true" />
      {children}
      {description ? <VisuallyHidden>{` — ${description}`}</VisuallyHidden> : null}
      {onClick ? (
        <span className="dpr-status-pill__caret" aria-hidden="true">
          <Icon name="chevronDown" size={12} />
        </span>
      ) : null}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        className={`dpr-status-pill dpr-status-pill--${tone}`}
        onClick={onClick}
        aria-expanded={ariaExpanded}
      >
        {body}
      </button>
    );
  }
  return <span className={`dpr-status-pill dpr-status-pill--${tone}`}>{body}</span>;
}
